import React from "react";
import { render, screen, waitFor } from "@testing-library/react";
import "@testing-library/jest-dom";
import { MemoryRouter, Route, Routes } from "react-router-dom";
import PublicCanvasViewer from "./PublicCanvasViewer";
import { consumeAuthReturnTarget } from "../../../utils/authReturnTarget";

const mockAuthFetch = jest.fn();
jest.mock("../../../utils/useAuthFetch", () => ({
  useAuthFetch: () => mockAuthFetch,
}));
jest.mock("../components/canvasBuilder", () => ({
  CanvasRenderer: ({
    title,
    elements,
  }: {
    title: string;
    elements: Array<{ id: string; effectiveAccess?: string }>;
  }) => (
    <div data-testid="canvas-renderer">
      {title}
      {elements.map((element) => (
        <span key={element.id} data-testid={element.id}>
          {element.effectiveAccess}
        </span>
      ))}
    </div>
  ),
  EmbeddedCanvasRenderer: () => <div data-testid="embedded-canvas" />,
}));
jest.mock("../components/canvasBuilder/CanvasRuntimeContext", () => ({
  CanvasRuntimeProvider: ({ children }: { children: React.ReactNode }) => (
    <div>{children}</div>
  ),
}));

const renderViewer = () =>
  render(
    <MemoryRouter initialEntries={["/public/canvases/canvas-1"]}>
      <Routes>
        <Route
          path="/public/canvases/:canvasId"
          element={<PublicCanvasViewer />}
        />
      </Routes>
    </MemoryRouter>,
  );

const canvas = {
  id: "canvas-1",
  workspace_id: "workspace-1",
  name: "Published Canvas",
  elements: [
    {
      id: "el-1",
      element_type: "TEXT",
      effective_access: "read",
      config_json: { text: "Hello" },
    },
  ],
};

describe("PublicCanvasViewer", () => {
  const originalLocation = window.location;
  const mockReplace = jest.fn();

  beforeEach(() => {
    mockAuthFetch.mockReset();
    mockReplace.mockReset();
    sessionStorage.clear();
    delete (window as any).location;
    (window as any).location = {
      ...originalLocation,
      pathname: "/public/canvases/canvas-1",
      search: "?tab=full",
      hash: "#top",
      replace: mockReplace,
    };
  });

  afterEach(() => {
    delete (window as any).location;
    (window as any).location = originalLocation;
  });

  it("renders an authorized published canvas", async () => {
    mockAuthFetch.mockResolvedValueOnce(canvas);
    renderViewer();
    expect(await screen.findByTestId("canvas-renderer")).toHaveTextContent(
      "Published Canvas",
    );
    expect(mockAuthFetch).toHaveBeenCalledWith(
      "/api/public/canvases/canvas-1",
      "fetch published canvas",
    );
  });

  it("returns to the same published canvas after sign-in", async () => {
    mockAuthFetch.mockRejectedValueOnce(
      Object.assign(new Error("Sign-in required"), { status: 401 }),
    );
    const view = renderViewer();
    await waitFor(() =>
      expect(mockReplace).toHaveBeenCalledWith(
        "/auth/return?to=%2Fpublic%2Fcanvases%2Fcanvas-1%3Ftab%3Dfull%23top",
      ),
    );
    expect(
      consumeAuthReturnTarget(
        "?to=%2Fpublic%2Fcanvases%2Fcanvas-1%3Ftab%3Dfull%23top",
      ),
    ).toBe("/public/canvases/canvas-1?tab=full#top");
    view.unmount();
    mockAuthFetch.mockResolvedValueOnce(canvas);
    renderViewer();
    expect(await screen.findByTestId("canvas-renderer")).toHaveTextContent(
      "Published Canvas",
    );
  });

  it.each([
    ["wrong workspace", 403],
    ["no canvas grant", 404],
    ["closed link", 410],
  ])("shows an unavailable state for %s", async (_case, status) => {
    mockAuthFetch.mockRejectedValueOnce(
      Object.assign(new Error("Denied"), { status }),
    );
    renderViewer();
    expect(
      await screen.findByText("Canvas not found or not available."),
    ).toBeInTheDocument();
    expect(mockReplace).not.toHaveBeenCalled();
  });

  it("omits denied nested elements from the published runtime projection", async () => {
    mockAuthFetch.mockResolvedValueOnce({
      ...canvas,
      elements: [
        {
          id: "visible",
          element_type: "TEXT",
          effective_access: "read",
          config_json: { text: "Visible" },
        },
        { id: "hidden", element_type: "CONTAINER", effective_access: "none" },
        {
          id: "hidden-child",
          parent_element_id: "hidden",
          element_type: "TEXT",
          effective_access: "read-write",
        },
      ],
    });
    renderViewer();
    expect(await screen.findByTestId("visible")).toHaveTextContent("read");
    expect(screen.queryByTestId("hidden")).toBeNull();
    expect(screen.queryByTestId("hidden-child")).toBeNull();
  });

  it("shows a clear empty state for a published canvas without elements", async () => {
    mockAuthFetch.mockResolvedValueOnce({ ...canvas, elements: [] });
    renderViewer();
    expect(
      await screen.findByText("Nothing has been published yet."),
    ).toBeInTheDocument();
  });
});
